import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react';

import * as api from '@/features/backlog/api';
import { useIdentity } from '@/features/identity/identity';

export interface SavedSearch {
  id: string;
  name: string;
  q: string;
  /** The results-table view, so a shared/recalled search reproduces columns + sort exactly.
   *  Raw URL-param strings (see features/search/columns encodeVisibleCols + the page's sort code). */
  cols?: string;
  sort?: string;
  created_at?: string;
}

interface SavedSearchesCtx {
  searches: SavedSearch[];
  save: (name: string, q: string, view?: { cols?: string; sort?: string }) => Promise<void>;
  remove: (id: string) => Promise<void>;
}

const Ctx = createContext<SavedSearchesCtx>({ searches: [], save: async () => {}, remove: async () => {} });

/** Personal saved global searches, persisted in the per-member preferences blob (so they're private
 *  and follow you across devices, unlike the shared board "saved views"). Loaded once on sign-in. */
export function SavedSearchesProvider({ children }: { children: ReactNode }) {
  const { me } = useIdentity();
  const [searches, setSearches] = useState<SavedSearch[]>([]);

  useEffect(() => {
    if (!me) {
      setSearches([]);
      return;
    }
    api.getMyPreferences().then((p) => setSearches(p.saved_searches ?? [])).catch(() => undefined);
  }, [me]);

  const save = useCallback(
    async (name: string, q: string, view?: { cols?: string; sort?: string }) => {
      const id = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `s_${Date.now()}`;
      setSearches((cur) => {
        const trimmed = name.trim() || q.trim();
        // Overwrite an existing save with the same name (case-insensitive), else append.
        const without = cur.filter((s) => s.name.toLowerCase() !== trimmed.toLowerCase());
        const next = [...without, { id, name: trimmed, q, cols: view?.cols, sort: view?.sort, created_at: new Date().toISOString() }];
        api.saveMyPreferences({ saved_searches: next }).catch(() => undefined);
        return next;
      });
    },
    [],
  );

  const remove = useCallback(
    async (id: string) => {
      setSearches((cur) => {
        const next = cur.filter((s) => s.id !== id);
        api.saveMyPreferences({ saved_searches: next }).catch(() => undefined);
        return next;
      });
    },
    [],
  );

  const value = useMemo(() => ({ searches, save, remove }), [searches, save, remove]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useSavedSearches = (): SavedSearchesCtx => useContext(Ctx);
